// src/stores/notificationStore.ts
// Global notification state with persistence and WebSocket integration
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type NotificationType =
  | 'task_created'
  | 'task_started'
  | 'task_progress'
  | 'task_completed'
  | 'task_failed'
  | 'task_cancelled'
  | 'phase_complete'
  | 'qa_audit'
  | 'deploy_started'
  | 'deploy_success'
  | 'deploy_failed'
  | 'error'
  | 'warning'
  | 'info'
  | 'system';

export type NotificationPriority = 'low' | 'normal' | 'high' | 'critical';

export interface Notification {
  id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  taskId?: string;
  phase?: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
  read: boolean;
  dismissed: boolean;
}

interface NotificationFilters {
  types: NotificationType[];
  priorities: NotificationPriority[];
  showRead: boolean;
  searchQuery: string;
}

interface NotificationStore {
  notifications: Notification[];
  unreadCount: number;
  filters: NotificationFilters;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read' | 'dismissed' | 'priority'> & { priority?: NotificationPriority }) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
  clearOld: (olderThanDays?: number) => void;
  setFilters: (filters: Partial<NotificationFilters>) => void;

  // Getters
  getFiltered: () => Notification[];
  getByTask: (taskId: string) => Notification[];
  getRecent: (limit?: number) => Notification[];
}

// Notification type to priority mapping
const TYPE_PRIORITY: Record<NotificationType, NotificationPriority> = {
  task_created: 'low',
  task_started: 'normal',
  task_progress: 'low',
  task_completed: 'normal',
  task_failed: 'high',
  task_cancelled: 'normal',
  phase_complete: 'normal',
  qa_audit: 'high',
  deploy_started: 'normal',
  deploy_success: 'high',
  deploy_failed: 'critical',
  error: 'high',
  warning: 'normal',
  info: 'low',
  system: 'normal',
};

// Notification type to icon/color mapping (for UI)
export const NOTIFICATION_STYLES: Record<NotificationType, { icon: string; color: string; bgColor: string }> = {
  task_created: { icon: '📝', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  task_started: { icon: '🚀', color: 'text-cyan-400', bgColor: 'bg-cyan-500/10' },
  task_progress: { icon: '⏳', color: 'text-purple-400', bgColor: 'bg-purple-500/10' },
  task_completed: { icon: '✅', color: 'text-green-400', bgColor: 'bg-green-500/10' },
  task_failed: { icon: '❌', color: 'text-red-400', bgColor: 'bg-red-500/10' },
  task_cancelled: { icon: '🚫', color: 'text-gray-400', bgColor: 'bg-gray-500/10' },
  phase_complete: { icon: '🎯', color: 'text-yellow-400', bgColor: 'bg-yellow-500/10' },
  qa_audit: { icon: '🔍', color: 'text-orange-400', bgColor: 'bg-orange-500/10' },
  deploy_started: { icon: '📦', color: 'text-indigo-400', bgColor: 'bg-indigo-500/10' },
  deploy_success: { icon: '🎉', color: 'text-green-400', bgColor: 'bg-green-500/10' },
  deploy_failed: { icon: '💥', color: 'text-red-400', bgColor: 'bg-red-500/10' },
  error: { icon: '⚠️', color: 'text-red-400', bgColor: 'bg-red-500/10' },
  warning: { icon: '⚡', color: 'text-yellow-400', bgColor: 'bg-yellow-500/10' },
  info: { icon: 'ℹ️', color: 'text-blue-400', bgColor: 'bg-blue-500/10' },
  system: { icon: '🔧', color: 'text-gray-400', bgColor: 'bg-gray-500/10' },
};

const DEFAULT_FILTERS: NotificationFilters = {
  types: [],
  priorities: [],
  showRead: true,
  searchQuery: '',
};

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      filters: DEFAULT_FILTERS,

      addNotification: (notification) => {
        const newNotif: Notification = {
          ...notification,
          id: `notif_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          timestamp: new Date().toISOString(),
          read: false,
          dismissed: false,
          priority: notification.priority || TYPE_PRIORITY[notification.type] || 'normal',
        };

        set((state) => ({
          notifications: [newNotif, ...state.notifications].slice(0, 500), // Keep max 500
          unreadCount: state.unreadCount + 1,
        }));

        // Browser notification for high priority items
        if (newNotif.priority === 'high' || newNotif.priority === 'critical') {
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(newNotif.title, {
              body: newNotif.message,
              icon: '/favicon.ico',
            });
          }
        }
      },

      markAsRead: (id) => {
        set((state) => {
          const notif = state.notifications.find((n) => n.id === id);
          if (!notif || notif.read) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      dismiss: (id) => {
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, dismissed: true, read: true } : n
          ),
          unreadCount: state.notifications.find((n) => n.id === id && !n.read)
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount,
        }));
      },

      dismissAll: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, dismissed: true, read: true })),
          unreadCount: 0,
        }));
      },

      clearOld: (olderThanDays = 7) => {
        const cutoff = Date.now() - olderThanDays * 24 * 60 * 60 * 1000;
        set((state) => {
          const kept = state.notifications.filter(
            (n) => new Date(n.timestamp).getTime() > cutoff || !n.read
          );
          return {
            notifications: kept,
            unreadCount: kept.filter((n) => !n.read).length,
          };
        });
      },

      setFilters: (filters) => {
        set((state) => ({
          filters: { ...state.filters, ...filters },
        }));
      },

      getFiltered: () => {
        const { notifications, filters } = get();
        return notifications.filter((n) => {
          if (n.dismissed) return false;
          if (!filters.showRead && n.read) return false;
          if (filters.types.length > 0 && !filters.types.includes(n.type)) return false;
          if (filters.priorities.length > 0 && !filters.priorities.includes(n.priority)) return false;
          if (filters.searchQuery) {
            const q = filters.searchQuery.toLowerCase();
            return (
              n.title.toLowerCase().includes(q) ||
              n.message.toLowerCase().includes(q) ||
              n.taskId?.toLowerCase().includes(q)
            );
          }
          return true;
        });
      },

      getByTask: (taskId) => {
        return get().notifications.filter((n) => n.taskId === taskId && !n.dismissed);
      },

      getRecent: (limit = 10) => {
        return get()
          .notifications.filter((n) => !n.dismissed)
          .slice(0, limit);
      },
    }),
    {
      name: 'controldeck-notifications',
      partialize: (state) => ({
        notifications: state.notifications.slice(0, 200), // Only persist last 200
      }),
    }
  )
);

// Helper to create notifications from WebSocket events
export function notificationFromWSEvent(event: {
  type: string;
  task_id?: string;
  description?: string;
  status?: string;
  phase?: string;
  progress?: number;
  error?: string;
  result?: unknown;
}): Omit<Notification, 'id' | 'timestamp' | 'read' | 'dismissed'> | null {
  const taskId = event.task_id;
  const desc = event.description || 'Task';

  switch (event.type) {
    case 'task_created':
      return {
        type: 'task_created',
        priority: 'low',
        title: 'Task Created',
        message: desc,
        taskId,
      };

    case 'task_updated':
    case 'task_progress':
      if (event.status === 'IN_PROGRESS' && event.phase) {
        return {
          type: 'phase_complete',
          priority: 'normal',
          title: `Phase: ${event.phase}`,
          message: `${desc} - ${event.progress || 0}%`,
          taskId,
          phase: event.phase,
          metadata: { progress: event.progress },
        };
      }
      return null; // Skip regular progress updates

    case 'task_completed':
      return {
        type: 'task_completed',
        priority: 'normal',
        title: 'Task Completed',
        message: desc,
        taskId,
        metadata: { result: event.result },
      };

    case 'task_failed':
      return {
        type: 'task_failed',
        priority: 'high',
        title: 'Task Failed',
        message: event.error || desc,
        taskId,
        metadata: { error: event.error },
      };

    case 'deploy_started':
      return {
        type: 'deploy_started',
        priority: 'normal',
        title: 'Deployment Started',
        message: desc,
        taskId,
      };

    case 'deploy_success':
      return {
        type: 'deploy_success',
        priority: 'high',
        title: 'Deployment Successful',
        message: desc,
        taskId,
      };

    case 'deploy_failed':
      return {
        type: 'deploy_failed',
        priority: 'critical',
        title: 'Deployment Failed',
        message: event.error || desc,
        taskId,
        metadata: { error: event.error },
      };

    default:
      return null;
  }
}
